/**
 * EF-SUPERADMIN-TENANT-SWITCH-V1: Tenant Switcher Dropdown
 * 
 * Dropdown component for superadmins to switch between tenant contexts.
 * Shows "Platform Mode" when no tenant is selected.
 */

import React from 'react';
import { useTenantContext } from '../../context/TenantContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Badge } from '../ui/badge';
import {
  Building2,
  ChevronDown,
  Globe,
  Check,
  Loader2,
} from 'lucide-react';

export default function TenantSwitcher() {
  const {
    isSuperAdmin,
    selectedTenantId,
    selectedTenant,
    tenantList,
    loading,
    setTenant,
    clearTenant,
    contextLabel,
    isViewingTenant,
  } = useTenantContext();

  // Only show for superadmins
  if (!isSuperAdmin) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`gap-2 ${isViewingTenant ? 'border-amber-500/50 bg-amber-500/10 text-amber-600 dark:text-amber-400' : ''}`}
          data-testid="tenant-switcher"
        >
          {isViewingTenant ? (
            <Building2 className="h-4 w-4" />
          ) : (
            <Globe className="h-4 w-4" />
          )}
          <span className="max-w-[150px] truncate">
            {contextLabel}
          </span>
          <ChevronDown className="h-3 w-3 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="end" className="w-[220px]">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          View Context
        </DropdownMenuLabel>
        
        {/* Platform Mode Option */}
        <DropdownMenuItem
          onClick={clearTenant}
          className="gap-2"
          data-testid="tenant-option-platform"
        >
          <Globe className="h-4 w-4" />
          <span className="flex-1">Platform Mode</span>
          {!isViewingTenant && (
            <Check className="h-4 w-4 text-primary" />
          )}
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Tenants
        </DropdownMenuLabel>
        
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : tenantList.length === 0 ? (
          <div className="px-2 py-4 text-center text-sm text-muted-foreground">
            No tenants found
          </div>
        ) : (
          tenantList.map((tenant) => (
            <DropdownMenuItem
              key={tenant.id || tenant.company_id}
              onClick={() => setTenant(tenant.company_id, tenant)}
              className="gap-2"
              data-testid={`tenant-option-${tenant.company_id}`}
            >
              <Building2 className="h-4 w-4" />
              <span className="flex-1 truncate">{tenant.company_name}</span>
              {selectedTenantId === tenant.company_id && (
                <Check className="h-4 w-4 text-primary" />
              )}
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
